/**
 * Which comparisons an approval-rule condition can make on each field.
 *
 * A master-record field (vendor, department…) holds an id, and an id has no
 * order: "vendor is at least Zenith" cannot be evaluated, so the engine finds
 * no number and the condition silently never matches — the rule is dead and
 * whatever it was meant to catch is auto-approved. Only numbers can be ordered.
 */
export const REFERENCE_CONDITION_FIELDS = [
    'vendorId',
    'departmentId',
    'locationId',
    'regionId',
    'verticalId',
    'businessUnitId',
];
/** Fields compared as numbers. */
export const NUMERIC_CONDITION_FIELDS = [
    'amount',
    'employeeCount',
];
/** Comparisons that need an ordered (numeric) value. */
export const ORDERING_OPERATORS = [
    'gte',
    'gt',
    'lte',
    'lt',
    'between',
];
/** Comparisons any field supports. */
export const EQUALITY_OPERATORS = [
    'eq',
    'ne',
    'in',
    'nin',
];
export function isReferenceConditionField(field) {
    return REFERENCE_CONDITION_FIELDS.includes(field);
}
export function isNumericConditionField(field) {
    return NUMERIC_CONDITION_FIELDS.includes(field);
}
/** The operators a field accepts, in the order a picker should offer them. */
export function conditionOperators(field) {
    return isNumericConditionField(field)
        ? [...ORDERING_OPERATORS, ...EQUALITY_OPERATORS]
        : EQUALITY_OPERATORS;
}
export function isOperatorAllowed(field, operator) {
    return conditionOperators(field).includes(operator);
}
/**
 * The operator a condition should have once its field changes: the current one
 * when the new field accepts it, otherwise the field's first operator ("is").
 */
export function operatorForField(field, current) {
    return isOperatorAllowed(field, current)
        ? current
        : conditionOperators(field)[0];
}
/**
 * Amounts that no *general* rule covers — the bands an item falls through
 * to auto-approval when it matches none of the vendor, department or other
 * specific rules.
 *
 * A general rule is an active one whose conditions are all on the amount (a
 * rule with none covers everything). Amounts are whole minor units, so open
 * bounds close to the next unit: "above ₹1L" starts at ₹1,00,000.01.
 */
export function uncoveredAmounts(rules, appliesTo) {
    const covered = [];
    for (const rule of rules) {
        if (!rule.active || rule.appliesTo !== appliesTo)
            continue;
        if (!rule.conditions.every((condition) => condition.field === 'amount'))
            continue;
        const range = intersect(rule.conditions);
        if (range)
            covered.push(range);
    }
    covered.sort((a, b) => a.low - b.low);
    const gaps = [];
    let next = 0; // the lowest amount not yet known to be covered
    for (const range of covered) {
        if (range.low > next)
            gaps.push({ low: next, high: range.low - 1 });
        if (range.high === null)
            return gaps;
        next = Math.max(next, range.high + 1);
    }
    gaps.push({ low: next, high: null });
    return gaps;
}
/** The amounts one rule's amount conditions all accept, or null for none (or none understood). */
function intersect(conditions) {
    let low = 0;
    let high = null;
    const below = (limit) => {
        high = high === null ? limit : Math.min(high, limit);
    };
    for (const { operator, value } of conditions) {
        const n = Number(Array.isArray(value) ? value[0] : value);
        switch (operator) {
            case 'gt':
                low = Math.max(low, Math.floor(n) + 1);
                break;
            case 'gte':
                low = Math.max(low, Math.ceil(n));
                break;
            case 'lt':
                below(Math.ceil(n) - 1);
                break;
            case 'lte':
                below(Math.floor(n));
                break;
            case 'eq':
                low = Math.max(low, n);
                below(n);
                break;
            case 'between': {
                const [from, to] = (Array.isArray(value) ? value : []).map(Number);
                low = Math.max(low, Math.ceil(from ?? 0));
                below(Math.ceil(to ?? 0) - 1); // upper bound exclusive, as the engine reads it
                break;
            }
            default:
                // `ne` / `in` / `nin` on an amount: too irregular to call a band.
                return null;
        }
    }
    return high !== null && high < low ? null : { low, high };
}
//# sourceMappingURL=approvalConditions.js.map